import React, { Component } from "react";
import { connect } from "react-redux";
import MatterPermissionsEditor from "./MatterPermissionsEditor";
import SelfLockoutWarning from "./SelfLockoutWarning";
import {
	numberWithCommas,
	makeDebouncedFunction,
} from "../utilities/utilities";
import { IconAlertTriangle } from "@tabler/icons-react";
import { Loader } from "@mantine/core";
import { LabeledTextInput } from "./TextInput";
import { LabeledSwitch } from "./AsyncSwitch";

class BulkMatterPermissionsUnconnected extends Component {
	constructor(props) {
		super(props);
		this.state = {
			// Permission state - will be managed by MatterPermissionsEditor
			permissions: {
				matterPermissionGlobalSetting: "allUsers",
				userAllowList: props.userAllowList || [],
				groupAllowList: props.groupAllowList || [],
			},
			// Bulk-specific state
			confirmTextBox: "",
			ignoreMattersThatAlreadyHavePermissions: false,
		};
		this.handlePermissionsChange = this.handlePermissionsChange.bind(this);
		this.handleChangeHere = makeDebouncedFunction(
			this.handleChangeHere.bind(this),
			300
		);
	}

	handlePermissionsChange(permissions) {
		this.setState({ permissions }, () => {
			this.notifyParent();
		});
	}

	handleChangeHere(key, value) {
		this.setState({ [key]: value }, () => {
			this.notifyParent();
		});
	}

	notifyParent() {
		// Send the complete bulk matter permissions object to parent
		this.props.onChange("matterPermissionsOutputObject", {
			...this.state.permissions,
			matterIDsToApplyPermissionsTo:
				this.props.matterIDsToApplyPermissionsTo,
			ignoreMattersThatAlreadyHavePermissions:
				this.state.ignoreMattersThatAlreadyHavePermissions,
			confirmed:
				this.state.confirmTextBox &&
				this.state.confirmTextBox.toLowerCase() === "confirm",
			allMatchingMatterIDsWithPermissions:
				this.props.allMatchingMatterIDsWithPermissions,
			numMatterPermissionsToBeUpdated: this.state
				.ignoreMattersThatAlreadyHavePermissions
				? this.props.matterIDsToApplyPermissionsTo.length -
					this.props.allMatchingMatterIDsWithPermissions.length
				: this.props.matterIDsToApplyPermissionsTo.length,
		});
	}

	render() {
		const {
			allMatchingMatterIDsWithPermissions,
			matterIDsToApplyPermissionsTo,
			hasValidActiveFilter,
			numFilteredMattersString,
			numAllMattersString,
			clientName,
		} = this.props;

		const numMatterPermissionsToBeUpdated = this.state
			.ignoreMattersThatAlreadyHavePermissions
			? `${numberWithCommas(matterIDsToApplyPermissionsTo.length - allMatchingMatterIDsWithPermissions.length)}`
			: `${numberWithCommas(matterIDsToApplyPermissionsTo.length)}`;

		let matterPermissionsMessage = "";

		if (hasValidActiveFilter) {
			matterPermissionsMessage = `Edit permissions for visible matters${clientName ? ` for ${clientName}` : ""}. You are about to change the permissions for ${numFilteredMattersString} of ${numAllMattersString} total matters (all matters included in the current view)${clientName ? ` for ${clientName}` : ""}.  This will affect all users with access to these matters.`;
		} else {
			matterPermissionsMessage = `Edit permissions for ALL matters${clientName ? ` for ${clientName}` : ""}. You are about to change the permissions for ALL ${numAllMattersString} matters ${clientName ? ` for ${clientName}` : ""}.  This will affect all users with access to these matters.`;
		}

		// this loading prop is in the bulkMatterPermissionsObject that is passed into openGlobalDialog
		if (this.props.loading) {
			return <Loader />;
		}
		return (
			<div>
				<div style={{ marginBottom: 10 }}>
					{matterPermissionsMessage}
				</div>
				{allMatchingMatterIDsWithPermissions.length > 0 ? (
					<div>
						<div
							style={{
								borderRadius: 5,
								backgroundColor: "var(--lw-gold)",
								display: "flex",
								alignItems: "center",
							}}
						>
							<IconAlertTriangle
								style={{
									color: "white",
									margin: 10,
								}}
							/>{" "}
							Explicit Permissions for{" "}
							{allMatchingMatterIDsWithPermissions.length}{" "}
							matter(s) will be{" "}
							{this.state.ignoreMattersThatAlreadyHavePermissions
								? "unchanged"
								: "overwritten"}
							.
						</div>
						<div>
							<LabeledSwitch
								upperLabel="Matter Permissions Update Option"
								label="Do not update permissions for Matters that already have explicit permissions"
								switchValue={
									!!this.state
										.ignoreMattersThatAlreadyHavePermissions
								}
								handleSwitchValueChange={() => {
									this.handleChangeHere(
										"ignoreMattersThatAlreadyHavePermissions",
										!this.state
											.ignoreMattersThatAlreadyHavePermissions
									);
								}}
							/>
						</div>
					</div>
				) : null}
				<div>
					<MatterPermissionsEditor
						labelType="MULTIPLE_MATTERS"
						initialPermissions={this.state.permissions}
						availableUsers={this.props.users}
						availableGroups={this.props.groups}
						onChange={this.handlePermissionsChange}
					/>
					<div style={{ marginBottom: 10 }}>
						{`Are you sure you want to continue? This will update permissions for ${numMatterPermissionsToBeUpdated} matters. Please type "confirm" in the box below to confirm and proceed.`}
					</div>
					<LabeledTextInput
						label=""
						name="confirmTextBox"
						defaultValue={this.state.confirmTextBox}
						placeholder="Type confirm here"
						onChange={this.handleChangeHere}
						autocomplete="off"
					/>
					<SelfLockoutWarning
						matterPermissionGlobalSetting={
							this.state.permissions.matterPermissionGlobalSetting
						}
						userAllowList={this.state.permissions.userAllowList}
						groupAllowList={this.state.permissions.groupAllowList}
						currentUserSub={this.props.userSub}
						permissionsGroups={this.props.groups}
						singular={false}
						isUptimeAdmin={this.props.isUptimeAdmin}
						mode="edit"
					/>
				</div>
			</div>
		);
	}
}

const BulkMatterPermissions = connect((state) => {
	return {
		userSub: state.user.cognitoUser.attributes.sub,
		isUptimeAdmin: state.user.isUptimeAdmin,
	};
})(BulkMatterPermissionsUnconnected);

export default BulkMatterPermissions;
